

import { ExpandMore as ChevronDown } from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { TimelineFilters as ITimelineFilters } from '@/lib/timeline-utils';
import type { EntitySummary, NarrativeStatus } from '@/lib/types';

interface TimelineFiltersProps {
  filters: ITimelineFilters;
  onFiltersChange: (filters: ITimelineFilters) => void;
  locations: EntitySummary[];
  characters: EntitySummary[];
  organizations: EntitySummary[];
}

export function TimelineFilters({
  filters,
  onFiltersChange,
  locations,
  characters,
  organizations,
}: TimelineFiltersProps) {
  const { t } = useTranslation();
  const toggleLocation = (locationId: string) => {
    const newLocationIds = filters.locationIds.includes(locationId)
      ? filters.locationIds.filter((id) => id !== locationId)
      : [...filters.locationIds, locationId];

    onFiltersChange({ ...filters, locationIds: newLocationIds });
  };

  const toggleCharacter = (characterId: string) => {
    const newCharacterIds = filters.characterIds.includes(characterId)
      ? filters.characterIds.filter((id) => id !== characterId)
      : [...filters.characterIds, characterId];

    onFiltersChange({ ...filters, characterIds: newCharacterIds });
  };

  const toggleOrganization = (organizationId: string) => {
    const newOrganizationIds = filters.organizationIds.includes(organizationId)
      ? filters.organizationIds.filter((id) => id !== organizationId)
      : [...filters.organizationIds, organizationId];

    onFiltersChange({ ...filters, organizationIds: newOrganizationIds });
  };

  const toggleNarrativeStatus = (status: NarrativeStatus) => {
    const newStatus = filters.narrativeStatus.includes(status)
      ? filters.narrativeStatus.filter((s) => s !== status)
      : [...filters.narrativeStatus, status];

    onFiltersChange({ ...filters, narrativeStatus: newStatus });
  };

  return (
    <Paper
      sx={{
        width: 280,
        borderRadius: 0,
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'auto',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('timeline.filters.title')}
        </Typography>

        {/* Locations */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
            <Typography variant="subtitle2">{t('timeline.filters.locations')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.includeSublocations}
                  onChange={(e) =>
                    onFiltersChange({ ...filters, includeSublocations: e.target.checked })
                  }
                  size="small"
                />
              }
              label={<Typography variant="caption">{t('timeline.filters.include_sublocations')}</Typography>}
              sx={{ mb: 1 }}
            />
            <FormGroup>
              {locations.slice(0, 10).map((location) => (
                <FormControlLabel
                  key={location.id}
                  control={
                    <Checkbox
                      checked={filters.locationIds.includes(location.id)}
                      onChange={() => toggleLocation(location.id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{location.name}</Typography>}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Characters */}
        <Accordion>
          <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
            <Typography variant="subtitle2">{t('timeline.filters.characters')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {characters.slice(0, 10).map((character) => (
                <FormControlLabel
                  key={character.id}
                  control={
                    <Checkbox
                      checked={filters.characterIds.includes(character.id)}
                      onChange={() => toggleCharacter(character.id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{character.name}</Typography>}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Organizations */}
        <Accordion>
          <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
            <Typography variant="subtitle2">{t('timeline.filters.organizations')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {organizations.slice(0, 10).map((organization) => (
                <FormControlLabel
                  key={organization.id}
                  control={
                    <Checkbox
                      checked={filters.organizationIds.includes(organization.id)}
                      onChange={() => toggleOrganization(organization.id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{organization.name}</Typography>}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Narrative Status */}
        <Accordion>
          <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
            <Typography variant="subtitle2">{t('timeline.filters.narrative_status')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.narrativeStatus.includes('notWritten')}
                    onChange={() => toggleNarrativeStatus('notWritten')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('timeline.filters.status_not_written')}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.narrativeStatus.includes('partial')}
                    onChange={() => toggleNarrativeStatus('partial')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('timeline.filters.status_partial')}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.narrativeStatus.includes('complete')}
                    onChange={() => toggleNarrativeStatus('complete')}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('timeline.filters.status_complete')}</Typography>}
              />
            </FormGroup>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );
}
